class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      if (!evt.target.closest('.cart-drawer-listo__inner')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    // When cart starts empty, <cart-drawer-items> isn't in the DOM and can't
    // subscribe to its own refresh. The drawer itself listens so the
    // empty → has-items transition repaints the drawer (otherwise an ATC
    // succeeds but the drawer keeps showing the empty state).
    this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // Skip when <cart-drawer-items> is present — it handles its own refresh.
      if (this.querySelector('cart-drawer-items')) return;
      this.refreshFromServer();
    });
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
  }

  refreshFromServer() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const incoming = html.querySelector('#CartDrawer');
        const current = this.querySelector('#CartDrawer');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
        this.classList.remove('is-empty');
        const inner = this.querySelector('.cart-drawer-listo__inner');
        if (inner) inner.classList.remove('is-empty');
      })
      .catch((e) => console.error('[cart-drawer] refresh failed', e));
  }

  setHeaderCartIconAccessibility() {
    // Use document-level event delegation so it doesn't matter whether
    // <cart-drawer> parses before or after #cart-icon-bubble. (In akc-south
    // the drawer is early in body so the header hasn't rendered yet at
    // constructor time.)
    const self = this;

    const eligibleTarget = (event) => {
      const target = event.target.closest('#cart-icon-bubble, [data-open-cart-drawer]');
      return target || null;
    };

    document.addEventListener('click', (event) => {
      const target = eligibleTarget(event);
      if (!target) return;
      event.preventDefault();
      self.open(target);
    });

    document.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE' && event.code.toUpperCase() !== 'ENTER') return;
      const target = eligibleTarget(event);
      if (!target) return;
      event.preventDefault();
      self.open(target);
    });

    // Best-effort ARIA upgrade once the icon is in DOM.
    const applyAria = () => {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return false;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      return true;
    };
    if (!applyAria()) {
      document.addEventListener('DOMContentLoaded', applyAria, { once: true });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.cart-drawer-listo__empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.cart-drawer-listo__inner') || this.querySelector('.cart-drawer-listo__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.cart-drawer-listo__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    // The header cart count is NOT a Shopify section in this theme — it lives
    // inline in snippets/user-nav.liquid and is updated via a cartUpdate
    // subscriber there. Don't request cart-icon-bubble here.
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer-listo__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// onKeyUpEscape helper (sourced from feetures-dawn global.js) — used by setSummaryAccessibility
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}
